import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { SortOrder } from 'antd/lib/table/interface';

export const createHandleHighlight = (setSearchQuery: Dispatch<SetStateAction<string>>) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };
}

export const highlightText = (text: string | number, searchText: string) => {
    if (!searchText) return text;
    if(!text) return "";
    const textStr = text.toString();
    const regex = new RegExp(`(${searchText})`, 'gi');
    const parts = textStr.split(regex);

    const partCounts: { [key: string]: number } = {};
    return parts.map((part) => {
        const partLower = part.toLowerCase();
        partCounts[partLower] = (partCounts[partLower] || 0) + 1;
        const key = `${partLower}-${partCounts[partLower]}`;

        return partLower === searchText.toLowerCase() ? (
            <mark key={key} className="highlight">{part}</mark>
        ) : (
            <span key={key}>{part}</span>
        );
    });
};

export const useTableScrollY = (offsetHeight = 365) => {
    const [tableScrollY, setTableScrollY] = useState(0);

    useEffect(() => {
        const updateTableScrollY = () => {
            setTableScrollY(window.innerHeight - offsetHeight);
        };

        updateTableScrollY();
        window.addEventListener("resize", updateTableScrollY);
        return () => {
            window.removeEventListener("resize", updateTableScrollY);
        };
    }, [offsetHeight]);

    return tableScrollY;
};

export const useFilteredData = <T extends unknown>(data: T[] | undefined) => {
    const [filteredData, setFilteredData] = useState(data);

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    return { filteredData, setFilteredData };
};

export const usePaginationConfig = <T extends unknown>(data: T[] | undefined) => {
    const [pagination, setPagination] = useState({
        pageSizeOptions: ['10', '20', '25', '50', '100'],
        showSizeChanger: true,
        defaultPageSize: 25,
    });

    const [forceRefresh, setForceRefresh] = useState(1);

    useEffect(() => {
        const totalEntries = data?.length || 0;
        const defaultPageSizeOptions = ['10', '20', '25', '50', '100'];
        const pageSizeOptionsNew = defaultPageSizeOptions.filter(option => Number(option) <= totalEntries);

        if (!pageSizeOptionsNew.includes(totalEntries.toString())) {
            pageSizeOptionsNew.push(totalEntries.toString());
        }

        const defaultPageSizeNew = totalEntries < 25 ? totalEntries : 25;

        setPagination({
            pageSizeOptions: pageSizeOptionsNew,
            showSizeChanger: true,
            defaultPageSize: defaultPageSizeNew,
        });
        setForceRefresh(prev => prev + 1)
    }, [data]);

    return { pagination, forceRefresh };
};

export const matchesSearchQuery = <T extends { [key: string]: any }>(row: T, searchText: string) => {
    return Object.keys(row).some(columnHeading => {
        if (columnHeading === 'id') return false;
        return row[columnHeading]?.toString().toLowerCase().includes(searchText);
    });
};

export const useSortOrder = () => {
    const [sortOrder, setSortOrder] = useState<{ columnKey: string | null, order: SortOrder | undefined }>({
        columnKey: null,
        order: undefined,
    });

    return { sortOrder, setSortOrder };
};

export const createHandleClose = (
    resetSearchAndFilters: () => void,
    setSortOrder: Dispatch<SetStateAction<{ columnKey: string | null, order: SortOrder | undefined }>>,
    onClose: () => void
) => {
    return () => {
        resetSearchAndFilters();
        setSortOrder({ columnKey: null, order: null });
        onClose();
    };
};
